import { type FC, type ReactNode } from 'react';

import { PersonSimpleRunIcon } from '../SceneBannerCard/icons';
import { UnstyledButton } from '../UnstyledButton';

import { HandSwipeLeftIcon } from './icons';
import styles from './IntroSlideContent.module.css';

export type IntroSlideKind = 'welcome' | 'scene';

type Props = {
  kind: IntroSlideKind;
  title: string | null;
  description: string | null;
  /** Required for `scene` slides — text on the "Explore X" CTA button. */
  buttonLabel?: string | null;
  /** Required for `scene` slides — fires the cinematic-+-scene-change flow. */
  onCtaClick?: () => void;
  /** Render-only customisation for the welcome slide's swipe-hint visual.
   *  Allows the parent to swap it out (e.g. for a desktop arrow hint).
   *  When omitted, the default Phosphor `HandSwipeLeft` icon is shown. */
  swipeHint?: ReactNode;
};

const DEFAULT_SWIPE_HINT = (
  <span className={styles.swipeIconWrap} aria-hidden="true">
    <HandSwipeLeftIcon className={styles.swipeIcon} />
  </span>
);

/**
 * One slide of the overall intro overlay. Two variants:
 *
 * - `welcome` — large title + description + animated hand-swipe-left hint
 *   that visually invites the user to swipe. No CTA button.
 *
 * - `scene` — same title/description plus a tooltip-styled "Explore X"
 *   button. Click fires `onCtaClick` (the parent runs the cinematic +
 *   scene-change flow).
 *
 * Title / description use `dir="auto"` so they layout correctly in RTL
 * (Arabic) without prop-drilling locale. The swipe-hint icon is mirrored
 * via CSS in RTL contexts (see `IntroSlideContent.module.css`).
 *
 * Enter / leave animations live on the PARENT's slot wrapper (see
 * `OverallSceneOverlay.module.css`) — this component itself is purely
 * static markup. That keeps two slides able to animate in parallel during
 * crossfade transitions.
 */
export const IntroSlideContent: FC<Props> = ({
  kind,
  title,
  description,
  buttonLabel,
  onCtaClick,
  swipeHint,
}) => {
  return (
    <div className={styles.slide}>
      <div className={styles.text}>
        {title && (
          <h2 className={styles.title} dir="auto">
            {title}
          </h2>
        )}
        {description && (
          <p className={styles.description} dir="auto">
            {description}
          </p>
        )}
      </div>

      {kind === 'welcome'
        ? (swipeHint ?? DEFAULT_SWIPE_HINT)
        : buttonLabel && onCtaClick && (
          <UnstyledButton
            className={styles.cta}
            onClick={onCtaClick}
            // Stop pointerdown so the curtain's swipe handler doesn't
            // interpret a button press as a swipe gesture origin.
            onPointerDown={(e) => e.stopPropagation()}
            // `dir="auto"` lets the button auto-detect direction from
            // its label content — Arabic content flips flex direction
            // so the icon swaps to the inline-end (RIGHT) side,
            // matching design 22540. English keeps icon on LEFT
            // (matching design 21960).
            dir="auto"
          >
            <span className={styles.ctaIcon} aria-hidden="true">
              <PersonSimpleRunIcon />
            </span>
            <span>{buttonLabel}</span>
          </UnstyledButton>
        )}
    </div>
  );
};

IntroSlideContent.displayName = 'IntroSlideContent';
